import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { validateSelectedVariant, updateVariantSelectorInTheDOM } from '../financeModalUtils';
import { ProductVariant } from '../../../../types';

interface VariantSelectorProps {
  className: string;
  variants: ProductVariant[];
  variantsLabel: string;
  selectedVariantCode: string;
  variantSelectorErrorMessage: string;
  setButtonIsDisabled: (s: boolean) => void;
  setSelectedVariantCode: (productCode: string) => void;
  setVariantSelectorErrorMessage: (errorMessage: string) => void;
}

function VariantSelector({
  variants,
  variantsLabel,
  className = '',
  selectedVariantCode,
  setButtonIsDisabled,
  setSelectedVariantCode,
  variantSelectorErrorMessage,
  setVariantSelectorErrorMessage
}: VariantSelectorProps): React.ReactElement {
  const variantInputPrefix = 'modal-';
  const ref = useRef<HTMLParagraphElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const variantCode = e.target.dataset.variant || '';
    setSelectedVariantCode(variantCode);
    const errorMessage = validateSelectedVariant(variants, variantCode);
    setVariantSelectorErrorMessage(errorMessage || '');
    setButtonIsDisabled(true);
    updateVariantSelectorInTheDOM(variantCode).then(() => {
      setButtonIsDisabled(false);
    });
  };

  useEffect(() => {
    if (variantSelectorErrorMessage) {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [variantSelectorErrorMessage]);

  return (
    <form className={className}>
      <div className="variant-selector__title">{variantsLabel}</div>
      <ul className="variant-selector__options">
        {variants.map(({ productCode, availableForPurchase, formatVariant }) => (
          <VariantOption
            key={productCode}
            id={`${variantInputPrefix}${productCode}`}
            label={formatVariant.split(', ')[0]}
            checked={selectedVariantCode === productCode}
            productCode={productCode}
            isAvailableForPurchase={availableForPurchase}
            handleChange={handleChange}
          />
        ))}
      </ul>
      {variantSelectorErrorMessage && (
        <p ref={ref} className={classnames('select__error', 'variant-selector__error-message')} role="alert">
          {variantSelectorErrorMessage}
        </p>
      )}
    </form>
  );
}

interface VariantOptionProps {
  id: string;
  name?: string;
  label?: string;
  value?: string;
  checked?: boolean;
  productCode?: string;
  isAvailableForPurchase?: boolean;
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function VariantOption({
  id,
  name = '',
  label = id,
  value = id,
  checked = false,
  productCode = id,
  handleChange = (): void => {},
  isAvailableForPurchase = true
}: VariantOptionProps): React.ReactElement {
  return (
    <li
      className={classnames('variant-selector__option', {
        'variant-selector__option--isNotAvailableForPurchase': !isAvailableForPurchase
      })}
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        data-variant={productCode}
        onChange={handleChange}
      />
      <label htmlFor={id}>{label}</label>
    </li>
  );
}

export default VariantSelector;
